import { MoreVertIcon } from '@tunasong/icons'
import styles from './entity-card.module.css'
import { dayjs, isAIGenerated } from '@tunasong/models'
import { features, useCurrentUser, useThunkDispatch } from '@tunasong/redux'
import type { Entity, EntityOrElement } from '@tunasong/schemas'
import { entityTypeNames, isPersisted } from '@tunasong/schemas'
import type { HTMLAttributes, JSX, MouseEvent } from 'react'
import React from 'react'
import { useDrop } from 'react-dnd'
import { useUIExtensions } from '../extensions.hook.js'
import { VBox } from '../layout/vertical-box.js'
import { Skeleton, Typography } from '../primitives/index.js'
import UserAvatar from '../profile/user-avatar.js'
import { SketchBadge } from './sketch-badge.js'
import { CardParentSpace } from './card-parent-space.js'
import { EntityIcon } from './entity-icon.js'
import { IconButton } from '../primitives/index.js'

/**
 * The card's media slot, which doubles as a drop target for adding a child
 * entity.
 *
 * This lives in its own component so that `useDrop` runs ONLY when the media
 * slot actually renders. react-dnd's `useDrop` throws
 * `Invariant Violation: Expected drag drop context` when no `DndProvider` is
 * mounted above it, so calling it at the top of EntityCard made react-dnd a
 * hard requirement of every card — for a drop target that is attached to the
 * DOM only when `cardMedia` or `children` is passed. Of the six EntityCard
 * call sites only the image plugin passes either; EntityGrid, the call path in
 * the production stack trace, passes neither. GH #1740.
 */
const CardMediaDropTarget: React.FC<{
  entityId: string
  hasCardImage: boolean
  onClick?: HTMLAttributes<HTMLDivElement>['onClick']
  children?: React.ReactNode
}> = ({ entityId, hasCardImage, onClick, children }) => {
  const dispatch = useThunkDispatch()

  const dropChild = async (childId?: string) => {
    if (!childId) {
      return
    }
    void dispatch(features.entities.thunks.addChild({ childId, parentId: entityId }))
  }

  const canDrop = (item: EntityOrElement) => Boolean(item?.id !== entityId)

  const [{ isHovering }, dropRef] = useDrop({
    accept: entityTypeNames as never,
    drop: item => {
      void dropChild(item.id)
    },
    canDrop,
    collect: monitor => ({
      isHovering: canDrop(monitor.getItem()) && monitor.isOver({ shallow: true }),
    }),
  })

  const mediaClasses = [
    styles.mediaContent,
    isHovering ? styles.dropHover : undefined,
    hasCardImage ? styles.mediaContentOverlay : undefined,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={mediaClasses} ref={dropRef as never} onClick={onClick}>
      {children}
    </div>
  )
}

export interface EntityCardProps<TElement extends Entity = Entity> extends HTMLAttributes<HTMLDivElement> {
  ref?: React.Ref<HTMLDivElement>
  /** @todo these actions should have a onClose handler */
  actions?: JSX.Element
  /** If not specified display a skeleton */
  entity?: TElement

  size?: 'small' | 'medium' | 'large' | 'tiny'

  cardMedia?: JSX.Element
  backgroundImage?: string

  /** Show the entity header. @default true */
  showHeader?: boolean
  /** Show menu. @default true */
  showMenu?: boolean
  /** Show secondary text. @default true */
  showSecondary?: boolean
  /** Top action button */
  headerAction?: JSX.Element | null
  /** Optional prefix added verbatim to the card's accessible name (e.g., "Pinned: "). */
  itemAriaLabelPrefix?: string
  /** Phase 2 dashboard: gate the last-editor avatar/dot. Defaults to true. */
  showLastEditor?: boolean
  /** Recently-updated grid: show a muted parent-space chip in the secondary line. @default false */
  showParentSpace?: boolean
}

export const EntityCard = (props: EntityCardProps) => {
  const {
    headerAction,
    cardMedia = null,
    showMenu = true,
    showHeader = true,
    showSecondary = true,
    actions,
    entity,
    backgroundImage: cardImage,
    children,
    ref,
    onClick,
    size,
    style: callerStyle,
    itemAriaLabelPrefix,
    showLastEditor = true,
    showParentSpace = false,
    ...restProps
  } = props

  const uiExtensions = useUIExtensions()

  const me = useCurrentUser()
  const hasExternalOwner = isPersisted(entity) ? entity.userId !== me.userId : false

  // Phase 2 dashboard pivot: surface the *last editor* alongside the owner — but
  // only when somebody ELSE edited last. A self-edit indicator is pure noise: in
  // "Continue working" / "Pinned" (your own recent items) every card would carry
  // one. Showing the avatar only for others' edits makes its presence meaningful
  // ("someone else touched this"). Nothing renders for your own edits, or when
  // the section opts out via `showLastEditor={false}` (solo-user path).
  const lastEditorId = isPersisted(entity) ? (entity.lastEditedBy ?? entity.userId) : undefined
  const isSelfEdit = lastEditorId === me.userId
  const showLastEditorAvatar = showLastEditor && lastEditorId !== undefined && !isSelfEdit


  const [menuRef, setMenuRef] = React.useState<null | HTMLElement>(null)
  const handleActions = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setMenuRef(ref => (ref === null ? event.currentTarget : null))
  }
  const handleCloseActions = () => {
    setMenuRef(null)
  }

  const handleContext = (ev: MouseEvent) => {
    ev.preventDefault()
  }

  // Shared min-height between the skeleton and the resolved card so swapping
  // from loading state to real content does not cause vertical layout shift
  // (Nielsen #1 — visibility of system status; jank works against trust).
  const cardMinHeight = showSecondary ? (size === 'large' ? 100 : 68) : size === 'large' ? 64 : 40

  if (!isPersisted(entity)) {
    return (
      <div className={styles.card} style={{ minHeight: cardMinHeight }}>
        <div className={styles.header} style={{ padding: size === 'large' ? 16 : 8 }}>
          <Skeleton variant="circular" width={size === 'large' ? 32 : 24} height={size === 'large' ? 32 : 24} />
          <div className={styles.headerContent}>
            <Skeleton variant="rounded" width="60%" height={size === 'large' ? 32 : 28} />
            {showSecondary ? <Skeleton variant="rounded" width="40%" height={size === 'large' ? 20 : 14} /> : null}
          </div>
        </div>
      </div>
    )
  }

  const subheader = entity ? `${dayjs(entity.updatedAt).fromNow()} ` : ''
  const showSketchBadge = isAIGenerated(entity)

  const hAction = headerAction ? (
    headerAction
  ) : headerAction !== null ? (
    <VBox className={styles.headerActions}>
      {hasExternalOwner && <UserAvatar userId={entity.userId} style={{ opacity: 0.3 }} size="small" />}

      <IconButton className={styles.menuButton} color="inherit" aria-label="settings" onClick={handleActions}>
        <MoreVertIcon />
      </IconButton>
    </VBox>
  ) : null

  const headerClasses = [styles.header, cardImage ? styles.headerOverlay : undefined].filter(Boolean).join(' ')

  return (
    <div
      className={[styles.card, cardImage ? styles.hasImage : undefined].filter(Boolean).join(' ')}
      ref={ref}
      onContextMenu={handleContext}
      style={{ minHeight: cardMinHeight, ...callerStyle }}
      {...restProps}
    >
      {cardImage ? <div className={styles.backgroundImage} style={{ backgroundImage: `url(${cardImage})` }} /> : null}

      {showHeader ? (
        // A11Y: header acts as a click target to open the entity. We use a div
        // with role="button" rather than a real <button> because the card has
        // grid/flex layout and contains other focusable children (the MoreVert
        // IconButton) — nesting a button inside a button would be invalid.
        // Keyboard handlers make this reachable for keyboard + screen-reader users.
        <div
          className={headerClasses}
          style={{ padding: size === 'large' ? 16 : 8 }}
          onClick={onClick}
          onKeyDown={
            onClick
              ? ev => {
                  if (ev.key === 'Enter' || ev.key === ' ') {
                    ev.preventDefault()
                    onClick(ev as unknown as React.MouseEvent<HTMLDivElement>)
                  }
                }
              : undefined
          }
          role={onClick ? 'button' : undefined}
          tabIndex={onClick ? 0 : undefined}
          data-entity-id={entity.id}
          aria-label={onClick ? `${itemAriaLabelPrefix ?? 'Open '}${entity.name ?? 'item'}` : undefined}
        >
          <EntityIcon entity={entity} size={size} />
          <div className={styles.headerContent}>
            <Typography variant={size === 'large' ? 'h6' : 'subtitle1'} component="div" className={styles.ellipsis}>
              {entity.name}
            </Typography>
            {showSecondary && subheader ? (
              <div className={styles.secondaryLine}>
                {/* CardParentSpace owns its trailing separator: it emits the space name and a
                 * " · " only when the entity has an ancestor space, so a top-level entity shows
                 * just the time with no orphan leading separator. */}
                {showParentSpace ? <CardParentSpace entity={entity} /> : null}
                <Typography
                  variant={size === 'large' ? 'body2' : 'caption'}
                  component="span"
                  className={styles.secondaryTime}
                >
                  {subheader}
                </Typography>
              </div>
            ) : null}
          </div>
          {showSketchBadge ? <SketchBadge size="sm" /> : null}
          {hAction}
        </div>
      ) : null}

      {(cardMedia || children) && (
        <CardMediaDropTarget entityId={entity.id} hasCardImage={Boolean(cardImage)} onClick={onClick}>
          {cardMedia}
          {children}
        </CardMediaDropTarget>
      )}
      {showLastEditorAvatar && lastEditorId ? (
        <div className={styles.lastEditorSlot}>
          <UserAvatar userId={lastEditorId} size="tiny" lastEditedContext />
        </div>
      ) : null}
      {actions && <div className={styles.actions}>{actions}</div>}

      {showMenu && menuRef && uiExtensions.entityList?.ItemMenu ? (
        <uiExtensions.entityList.ItemMenu entity={entity} onClose={handleCloseActions} anchorEl={menuRef} />
      ) : null}
    </div>
  )
}

export default EntityCard
